import { ref, type Ref } from 'vue';
import type { User, ImpersonationState } from '@/apiServices/types';
import userAPI from '@/apiServices/userAPI';
import { ActiveTenantService } from '@/services/ActiveTenantService';
import { type IAbstractPeriodicTask, AbstractPeriodicTask } from '@/generics/AbstractPeriodicTask';

//=================================================================================================
export interface ILoggedUserService extends IAbstractPeriodicTask {
  user: Ref<User|null>;
  impersonation: Ref<ImpersonationState | null>;
  getUserAsync(): Promise<User | null>;
  extendSessionAsync(): Promise<Date | null>;
  setSessionExpiration(value: Date | string): Date | null;
  getSessionExpiration(): Date | null;
  setSessionToken(value: string): string | null;
  getSessionToken(): string | null;
  logoutAsync(): Promise<void>;
  startImpersonationAsync(targetUserId: number): Promise<void>;
  stopImpersonationAsync(): Promise<void>;
  refreshImpersonationStateAsync(): Promise<void>;
}
//=================================================================================================
class LoggedUserServiceClass extends AbstractPeriodicTask implements ILoggedUserService {
  public user: Ref<User|null> = ref(null);
  public impersonation: Ref<ImpersonationState | null> = ref(null);

  // Singleton instance
  static instance: LoggedUserServiceClass = new LoggedUserServiceClass(30); // refresh every 30s

  //-----------------------------------------------------------------------------------------------
  private constructor(refreshInterval: number = 30) {
    super(refreshInterval); 
  }
  //-----------------------------------------------------------------------------------------------
  public static getInstance(): LoggedUserServiceClass {
    return LoggedUserServiceClass.instance;
  }
  //-----------------------------------------------------------------------------------------------
  public async getUserAsync(): Promise<User | null> {
    if (!this.user.value) {
      await this.run();
    }
    return this.user.value
  }
  //-----------------------------------------------------------------------------------------------
  public async extendSessionAsync(): Promise<Date | null> {
    try {

      const token: string | null = this.getSessionToken();

      if (!token) {
        return null;
      }

      const response = await userAPI.extendSession();
      if (response.status === 200) {

        const newExpirationTS: Date = new Date(response.data);

        this.setSessionExpiration(newExpirationTS);
        return newExpirationTS;
      }

    } catch {
      // Session extension is best-effort; callers handle a null return.
    }
    return null;
  }
  //-----------------------------------------------------------------------------------------------
  public setSessionExpiration(value: Date | string): Date | null {
    const expiration = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(expiration.getTime())) {
      sessionStorage.removeItem("sessionExpire");
      return null;
    }

    sessionStorage.setItem("sessionExpire", expiration.toISOString());
    return this.getSessionExpiration();
  }
  //-----------------------------------------------------------------------------------------------
  public getSessionExpiration(): Date | null {
    const s = sessionStorage.getItem("sessionExpire");
    if (s == null) {
      return null;
    }
    return new Date( s );
  }
  //-----------------------------------------------------------------------------------------------
  public setSessionToken(value: string): string | null {
    sessionStorage.setItem("sessionToken", value);
    return this.getSessionToken();
  }
  //-----------------------------------------------------------------------------------------------
  public getSessionToken(): string | null {
    return sessionStorage.getItem("sessionToken");
  }
  //-----------------------------------------------------------------------------------------------
  public async logoutAsync(): Promise<void> {
    try {

      const token: string | null = this.getSessionToken();

      if (!token) {
        return;
      }

      // If impersonating, revert to the real session first (best-effort) so the logout below ends
      // the underlying admin session rather than just the impersonation token, which would otherwise
      // leave the real session orphaned server-side.
      if (this.impersonation.value) {
        try {
          const stopResponse = await userAPI.stopImpersonation();
          if (stopResponse.status === 200 && stopResponse.data?.token) {
            this.setSessionToken(stopResponse.data.token);
          }
        } catch (error) {
          console.error('logoutAsync: failed to stop impersonation before logout', error);
        }
      }

      const response = await userAPI.logout();

      if (response.status === 200) {
        // Logout succeeded on the server; local session cleanup happens below.
      }
    } catch {
      // Still clear local session even if the server cannot be reached.
    }

    sessionStorage.clear();
    this.user.value = null;
    this.impersonation.value = null;
    // Reset tenant state so the login page's next requests carry no stale X-Tenant.
    ActiveTenantService.initFromUser(null);
  }
  //-----------------------------------------------------------------------------------------------
  // Impersonation (ADO #5044). The session-token swap on start/stop is what changes the app's
  // identity for every subsequent request - the user ref and impersonation ref are then refreshed
  // from the server via run().
  public async startImpersonationAsync(targetUserId: number): Promise<void> {
    try {
      const response = await userAPI.startImpersonation(targetUserId);
      if (response.status === 200 && response.data?.token) {
        this.setSessionToken(response.data.token);
        if (response.data.expirationTS) {
          this.setSessionExpiration(response.data.expirationTS);
        }
        await this.run();
        // The effective identity changed — tenant options, picker, and X-Tenant follow the target.
        ActiveTenantService.initFromUser(this.user.value);
      }
    } catch (error) {
      console.error('startImpersonationAsync failed', error);
      throw error; // rethrow original so the toast site can extract via getApiErrorMessage
    }
  }
  //-----------------------------------------------------------------------------------------------
  public async stopImpersonationAsync(): Promise<void> {
    try {
      const response = await userAPI.stopImpersonation();
      // 204 = wasn't impersonating; nothing to swap. Just clear local state and let run() refresh.
      if (response.status === 200 && response.data?.token) {
        this.setSessionToken(response.data.token);
        if (response.data.expirationTS) {
          this.setSessionExpiration(response.data.expirationTS);
        }
      }
      this.impersonation.value = null;
      await this.run();
      // Back to the admin's identity — restore their tenant state (and persisted choice).
      ActiveTenantService.initFromUser(this.user.value);
    } catch (error) {
      console.error('stopImpersonationAsync failed', error);
      throw error; // rethrow original so the toast site can extract via getApiErrorMessage
    }
  }
  //-----------------------------------------------------------------------------------------------
  public async refreshImpersonationStateAsync(): Promise<void> {
    try {
      const response = await userAPI.getImpersonationState();
      this.impersonation.value = response.data?.isImpersonating ? response.data : null;
    } catch (error) {
      console.error('refreshImpersonationStateAsync failed', error);
      this.impersonation.value = null;
    }
  }
  //-----------------------------------------------------------------------------------------------
  //
  // Implementation of abstract method run() of AbstractPeriodicTask
  //
  protected async run(): Promise<void> {

    const sessionToken: string | null = sessionStorage.getItem('sessionToken');

    // Stop refreshing if not logged in
    if (!sessionToken) {
      this.user.value = null;
      this.impersonation.value = null;
      this.stop(); // stop PeriodicTask
      return;
    }

    // Resolve user + impersonation independently: a role-less target 403s on getUserBySessionToken, but that must not abort the impersonation refresh the banner + Stop need (#5076).
    const [userResult] = await Promise.allSettled([
      userAPI.getUserBySessionToken(sessionToken),
      this.refreshImpersonationStateAsync(),
    ]);

    if (userResult.status === 'fulfilled' && userResult.value.status === 200) {
      this.user.value = userResult.value.data;
    } else {
      if (userResult.status === 'rejected') {
        console.error('LoggedUserService.run() failed to load user', userResult.reason);
      }
      this.user.value = null;
    }
  }
}
//-----------------------------------------------------------------------------------------------
// Export singleton instance
export const LoggedUserService: ILoggedUserService = LoggedUserServiceClass.getInstance();
// EOF
